import { z } from 'zod';
/**
 * Обязательные переменные окружения сервера.
 *
 * Схема живёт в contracts, а не в apps/api: конфигурация — такая же
 * граница процесса, как HTTP-запрос, и проверяется тем же способом
 * (CLAUDE.md, п. 6). Значений по умолчанию нет ни у одной переменной:
 * дефолт у секрета означает, что прод однажды поедет на значении
 * из примера (security-checklist.md, п. 1).
 */
/** Непустая строка: `FOO=` в .env — это отсутствующая переменная. */
const required = z.string().trim().min(1);
/**
 * DSN в формате Sentry: `протокол://ключ@хост/номер проекта` (D17).
 * Проверяется формой, а не доступностью: на старте никто никуда
 * не ходит, иначе недоступный мониторинг роняет приложение.
 */
const dsn = required.regex(/^https?:\/\/[^:@\s/]+@[^@\s/]+\/\d+$/, 'ожидается DSN вида https://ключ@glitchtip.example/1');
/**
 * Окружения развёртывания (D26).
 *
 * `NODE_ENV` их не различает и не должен: на стенде он тоже
 * `production` — сборка, минификация и поведение фреймворков там
 * обязаны совпадать с прод, иначе стенд перестаёт что-либо проверять.
 */
export const DEPLOY_ENVIRONMENTS = ['staging', 'production'];
/**
 * Транспорты писем (D34, D46).
 *
 * `smtp` был единственным настоящим, пока не выяснилось, что
 * облачные хостинги закрывают исходящий SMTP: со стенда молчат
 * и 587, и 465, и 2525. Порт 443 открыт всегда, поэтому появился
 * `http` — тот же интерфейс отправителя, запрос к API провайдера
 * вместо разговора по SMTP.
 *
 * SMTP не удалён: на хостинге, где он открыт, это по-прежнему
 * транспорт, не знающий имени провайдера (D34).
 */
export const MAIL_TRANSPORTS = ['log', 'smtp', 'http'];
/**
 * Значения по умолчанию нет намеренно. «Не задано — значит staging» —
 * это ровно та лазейка, через которую послабление мониторинга уезжает
 * в прод: достаточно один раз забыть переменную на сервере, и прод
 * тихо поедет без сбора ошибок (D26, security-checklist.md, п. 6).
 */
const deployEnv = z.enum(DEPLOY_ENVIRONMENTS, {
    error: `ожидается одно из: ${DEPLOY_ENVIRONMENTS.join(', ')}`,
});
const serverEnvShape = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']),
    DEPLOY_ENV: deployEnv,
    DATABASE_URL: required.regex(/^postgres(ql)?:\/\//, 'ожидается строка подключения вида postgres://…'),
    VALKEY_URL: required.regex(/^(valkey|redis|rediss):\/\//, 'ожидается строка подключения вида valkey://…'),
    API_HOST: required,
    API_PORT: z.coerce.number().int().min(1).max(65535),
    // Origin, а не URL: схема, хост и необязательный порт, без пути.
    // Проверка регулярным выражением, а не через глобальный URL: contracts
    // не зависит ни от рантайма браузера, ни от рантайма Node.
    WEB_ORIGIN: required.regex(/^https?:\/\/[^\s/?#]+$/, 'ожидается origin вида https://example.org (без пути)'),
    /**
     * DSN мониторинга ошибок (GlitchTip, D17).
     *
     * В production переменная обязательна: приложение без сбора ошибок
     * до первого пользователя — это ошибки, о которых узнаёшь от него
     * (security-checklist.md, пп. 1 и 6). В development и test она может
     * быть пустой — тогда отчёты никуда не отправляются, а пишутся
     * в лог, и разработке не нужен поднятый GlitchTip.
     */
    ERROR_MONITORING_DSN: dsn.optional(),
    /**
     * Секрет подписи access-токенов (D8, D35).
     *
     * Значения по умолчанию нет и быть не может: дефолт здесь означает,
     * что любой, кто читал репозиторий, выпишет себе токен мастера
     * (security-checklist.md, п. 1). Тридцать два знака — минимум под
     * HMAC-SHA256: ключ короче хеша не добавляет стойкости, а короткий
     * ключ ещё и подбирается.
     */
    AUTH_JWT_SECRET: required.min(32, 'секрет короче 32 знаков: под HMAC-SHA256 нужен ключ не короче хеша'),
    /**
     * Как отправляются письма (D34, D46): `http` — запросом к API
     * провайдера, `smtp` — по SMTP, `log` — в лог.
     */
    MAIL_TRANSPORT: z.enum(MAIL_TRANSPORTS, {
        error: `ожидается одно из: ${MAIL_TRANSPORTS.join(', ')}`,
    }),
    /** Отправитель писем: адрес, с которого уходит подтверждение почты. */
    MAIL_FROM: z.email('ожидается адрес электронной почты отправителя'),
    /**
     * Учётные данные SMTP: `smtp://пользователь:пароль@хост:порт`
     * либо `smtps://…` для неявного TLS. Обязательна при
     * `MAIL_TRANSPORT=smtp` и бессмысленна при `log`.
     */
    MAIL_SMTP_URL: required
        .regex(/^smtps?:\/\//, 'ожидается строка вида smtp://…')
        .optional(),
    /**
     * Адрес службы отправки провайдера (D46): полный URL метода.
     * Обязателен при `MAIL_TRANSPORT=http` и бессмыслен при остальных.
     */
    MAIL_HTTP_URL: required
        .regex(/^https?:\/\//, 'ожидается адрес вида https://…')
        .optional(),
    /**
     * Ключ доступа к API провайдера (D46).
     *
     * Отдельной переменной, а не внутри адреса: ключ в строке запроса
     * или в `https://ключ@хост` попадает в логи прокси и в сообщения
     * об ошибках разбора URL (security-checklist.md, п. 1).
     *
     * Только видимые символы ASCII: ключ уходит заголовком HTTP,
     * а `fetch` на букве вне латиницы бросает `TypeError` — и отказ
     * выглядит сетевым, то есть повторяется пять раз впустую
     * (`docs/quirks.md`). Проверка стоит здесь, чтобы опечатка
     * в секрете падала на старте, а не на первой регистрации.
     */
    MAIL_HTTP_KEY: required
        .regex(/^[\x21-\x7e]+$/, 'ключ уходит заголовком HTTP: допустимы только видимые знаки ASCII')
        .optional(),
});
/**
 * Правило «в проде DSN обязателен» вынесено отдельно: оно про пару
 * переменных, а не про одну.
 *
 * Смотрит на `DEPLOY_ENV`, а не на `NODE_ENV` (D26). При `staging`
 * отсутствие DSN допустимо: GlitchTip под прод ставится в фазе 3,
 * и до тех пор ошибки стенда видны только в логах контейнера. Это
 * послабление действует ровно при `staging` и нигде больше.
 */
const requireDsnInProduction = (env, ctx, variable) => {
    if (env.DEPLOY_ENV === 'production' && env.ERROR_MONITORING_DSN === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: [variable],
            message: 'переменная не задана, а при DEPLOY_ENV=production она обязательна: ' +
                'без неё ошибки никуда не отправляются (D26, security-checklist.md, п. 6)',
        });
    }
};
export const serverEnvSchema = serverEnvShape.superRefine((env, ctx) => {
    requireDsnInProduction(env, ctx, 'ERROR_MONITORING_DSN');
    // Транспорт `log` в проде — это прод, где никто не может подтвердить
    // почту и сбросить пароль, потому что письмо ушло в journalctl (D34).
    if (env.DEPLOY_ENV === 'production' && env.MAIL_TRANSPORT === 'log') {
        ctx.addIssue({
            code: 'custom',
            path: ['MAIL_TRANSPORT'],
            message: 'при DEPLOY_ENV=production письма не могут уходить в лог: ' +
                'подтверждение почты и сброс пароля перестают работать (D34)',
        });
    }
    // Обратное тоже проверяется: `smtp` без адреса сервера падает
    // не на старте, а на первом письме — то есть на первой регистрации.
    if (env.MAIL_TRANSPORT === 'smtp' && env.MAIL_SMTP_URL === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['MAIL_SMTP_URL'],
            message: 'переменная не задана, а при MAIL_TRANSPORT=smtp она обязательна: ' +
                'без неё письма некуда отправлять (D34)',
        });
    }
    // То же для HTTP, и по той же причине: узнать о незаданном ключе
    // на первой регистрации — значит узнать об этом от человека,
    // который не смог зарегистрироваться (D46).
    if (env.MAIL_TRANSPORT === 'http') {
        if (env.MAIL_HTTP_URL === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['MAIL_HTTP_URL'],
                message: 'переменная не задана, а при MAIL_TRANSPORT=http она обязательна: ' +
                    'без неё письма некуда отправлять (D46)',
            });
        }
        if (env.MAIL_HTTP_KEY === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['MAIL_HTTP_KEY'],
                message: 'переменная не задана, а при MAIL_TRANSPORT=http она обязательна: ' +
                    'без ключа провайдер отвергнет запрос (D46)',
            });
        }
    }
});
/**
 * Разбор окружения без выбрасывания исключения: вызывающий код решает,
 * как именно завершиться. Пустая строка приравнивается к отсутствию —
 * иначе `FOO=` в .env проходит проверку и падает уже в рантайме.
 */
export function parseServerEnv(source) {
    const result = serverEnvSchema.safeParse(dropEmpty(source));
    if (result.success)
        return { ok: true, env: result.data };
    return { ok: false, problems: toProblems(result.error) };
}
/** Пустая строка — это отсутствующая переменная, а не пустое значение. */
function dropEmpty(source) {
    const cleaned = {};
    for (const [key, value] of Object.entries(source)) {
        if (value !== undefined && value.trim() !== '')
            cleaned[key] = value;
    }
    return cleaned;
}
function toProblems(error) {
    return error.issues.map((issue) => ({
        variable: issue.path.join('.') || '(без имени)',
        message: issue.code === 'invalid_type' ? 'переменная не задана' : issue.message,
    }));
}
/**
 * Переменные окружения клиента.
 *
 * У клиента их три: режим сборки, окружение развёртывания и DSN
 * мониторинга. Значения подставляются на сборке (Vite) и проверяются
 * этой схемой при старте в браузере: собранный без DSN прод — это прод
 * без сбора клиентских ошибок, а он требуется (D26,
 * security-checklist.md, п. 6). Сама сборка на это не смотрит —
 * до неё то же самое проверяет развёртывание, шаг «Проверка настроек
 * развёртывания»: узнать о пустом DSN там дешевле, чем в браузере.
 */
const clientEnvShape = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']),
    DEPLOY_ENV: deployEnv,
    VITE_ERROR_MONITORING_DSN: dsn.optional(),
});
export const clientEnvSchema = clientEnvShape.superRefine((env, ctx) => {
    requireDsnInProduction({ DEPLOY_ENV: env.DEPLOY_ENV, ERROR_MONITORING_DSN: env.VITE_ERROR_MONITORING_DSN }, ctx, 'VITE_ERROR_MONITORING_DSN');
});
/** Разбор окружения клиента теми же правилами, что и серверного. */
export function parseClientEnv(source) {
    const result = clientEnvSchema.safeParse(dropEmpty(source));
    if (result.success)
        return { ok: true, env: result.data };
    return { ok: false, problems: toProblems(result.error) };
}
//# sourceMappingURL=env.js.map